import {
  ISupportMessage,
  ISupportMessagesResponse,
  ISupportTicket,
  ISupportTicketsResponse,
} from "../common/models/support/ISupport";
import { api } from "./api";

/** Сообщений в одной странице переписки */
export const SUPPORT_MESSAGES_PAGE_SIZE = 30;

export type SupportTicketsStatus = "active" | "finished" | "all";

// Чат поддержки глазами сотрудников площадки (/admin/support, docs/support-chat.md
// в репозитории бэкенда). Менеджеры отвечают только текстом: записи голосовых в
// панели нет по решению заказчика, голосовые пользователей можно только слушать.
export const supportApi = api
  .enhanceEndpoints({ addTagTypes: ["SupportTicket", "SupportMessages"] })
  .injectEndpoints({
    endpoints: (build) => ({
      getSupportTickets: build.query<
        ISupportTicketsResponse,
        {
          status: SupportTicketsStatus;
          dateFrom?: string;
          dateTo?: string;
          perPage?: number;
        }
      >({
        query({ status, dateFrom, dateTo, perPage }) {
          return {
            url: `/admin/support/tickets`,
            params: {
              status,
              date_from: dateFrom,
              date_to: dateTo,
              per_page: perPage,
            },
          };
        },
        providesTags: [{ type: "SupportTicket", id: "LIST" }],
      }),
      getSupportTicket: build.query<ISupportTicket, number>({
        query: (id) => ({ url: `/admin/support/tickets/${id}` }),
        providesTags: (result, error, id) => [{ type: "SupportTicket", id }],
        transformResponse: (result: { ticket: ISupportTicket }) =>
          result.ticket,
      }),
      // Последние сообщения пользователя вместе с прошлыми обращениями
      getSupportMessages: build.query<ISupportMessagesResponse, number>({
        query: (ticketId) => ({
          url: `/admin/support/tickets/${ticketId}/messages`,
          params: { with_history: 1, limit: SUPPORT_MESSAGES_PAGE_SIZE },
        }),
        providesTags: (result, error, ticketId) => [
          { type: "SupportMessages", id: ticketId },
        ],
      }),
      // Страница старше beforeId (кнопка «Показать более ранние»)
      getSupportMessagesBefore: build.query<
        ISupportMessagesResponse,
        { ticketId: number; beforeId: number }
      >({
        query: ({ ticketId, beforeId }) => ({
          url: `/admin/support/tickets/${ticketId}/messages`,
          params: {
            with_history: 1,
            limit: SUPPORT_MESSAGES_PAGE_SIZE,
            before_id: beforeId,
          },
        }),
      }),
      sendSupportMessage: build.mutation<
        ISupportMessage,
        { ticketId: number; text: string; clientId: string }
      >({
        query({ ticketId, text, clientId }) {
          return {
            url: `/admin/support/tickets/${ticketId}/messages`,
            method: "POST",
            body: { text, client_id: clientId },
          };
        },
        invalidatesTags: (result, error, { ticketId }) =>
          error
            ? []
            : [
                { type: "SupportMessages", id: ticketId },
                { type: "SupportTicket", id: "LIST" },
              ],
        transformResponse: (result: { message: ISupportMessage }) =>
          result.message,
      }),
      // Сообщения пользователя в обращении прочитаны: сбрасывает счётчики в списке
      markSupportTicketRead: build.mutation<void, number>({
        query: (ticketId) => ({
          url: `/admin/support/tickets/${ticketId}/read`,
          method: "POST",
        }),
        invalidatesTags: [{ type: "SupportTicket", id: "LIST" }],
      }),
      // Первое прослушивание голосового пользователя; переписка перечитывается, чтобы точка «не прослушано» погасла
      markSupportVoiceListened: build.mutation<
        void,
        { messageId: number; ticketId: number }
      >({
        query: ({ messageId }) => ({
          url: `/admin/support/messages/${messageId}/listened`,
          method: "POST",
        }),
        invalidatesTags: (result, error, { ticketId }) =>
          error ? [] : [{ type: "SupportMessages", id: ticketId }],
      }),
      closeSupportTicket: build.mutation<void, number>({
        query: (ticketId) => ({
          url: `/admin/support/tickets/${ticketId}/close`,
          method: "POST",
        }),
        invalidatesTags: (result, error, ticketId) =>
          error
            ? []
            : [
                { type: "SupportTicket", id: "LIST" },
                { type: "SupportTicket", id: ticketId },
              ],
      }),
      reopenSupportTicket: build.mutation<void, number>({
        query: (ticketId) => ({
          url: `/admin/support/tickets/${ticketId}/reopen`,
          method: "POST",
        }),
        invalidatesTags: (result, error, ticketId) =>
          error
            ? []
            : [
                { type: "SupportTicket", id: "LIST" },
                { type: "SupportTicket", id: ticketId },
              ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetSupportTicketsQuery,
  useGetSupportTicketQuery,
  useGetSupportMessagesQuery,
  useLazyGetSupportMessagesBeforeQuery,
  useSendSupportMessageMutation,
  useMarkSupportTicketReadMutation,
  useMarkSupportVoiceListenedMutation,
  useCloseSupportTicketMutation,
  useReopenSupportTicketMutation,
} = supportApi;
